import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CalendarDays, Notebook } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';
import { absoluteImageUrl } from '@/components/admin/ImageUploader';
import { FIELD_NOTE_CATEGORIES, formatDate, labelFor } from '@/lib/taxonomies';
import { useSeo } from '@/lib/seo';
import { RECAPS } from '@/constants/testIds';

export default function RecapDetailPage() {
  const { yearMonth } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: null, data: null });
    publicApi
      .recapDetail(yearMonth)
      .then((data) => {
        if (alive) setState({ loading: false, error: null, data });
      })
      .catch((err) => {
        if (alive)
          setState({
            loading: false,
            error: err?.response?.status === 404 ? 'not-found' : 'error',
            data: null,
          });
      });
    return () => {
      alive = false;
    };
  }, [yearMonth]);

  useSeo({
    title: state.data
      ? `${state.data.recap.title} | A Guy Who Hunts`
      : 'Season recap | A Guy Who Hunts',
    description:
      state.data?.recap?.intro?.slice(0, 160) ||
      'A monthly recap of Field Notes from a season of hunting public land.',
  });

  if (state.loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="h-6 w-40 rounded bg-[hsl(var(--muted))] animate-pulse" />
        <div className="mt-4 h-10 w-2/3 rounded bg-[hsl(var(--muted))] animate-pulse" />
        <div className="mt-10 space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (state.error === 'not-found' || !state.data) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-24 text-center">
        <p className="kicker">Recap not found</p>
        <h1 className="font-serif text-4xl tracking-tight mt-2">
          Nothing published for that month yet.
        </h1>
        <p className="mt-4 text-[hsl(var(--muted-foreground))]">
          Head back to the Field Notes archive or check the recap index.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <Link to="/field-notes">
            <Button variant="secondary">Field Notes</Button>
          </Link>
          <Link to="/recap">
            <Button>All recaps</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { recap, notes } = state.data;

  return (
    <div>
      <section className="relative border-b border-[hsl(var(--border))] hero-radial">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Link
            to="/recap"
            className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
          >
            <ArrowLeft size={14} /> All recaps
          </Link>
          <p className="kicker mt-6">Monthly recap</p>
          <h1
            data-testid={RECAPS.detailTitle}
            className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 leading-tight"
          >
            {recap.title}.
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-[hsl(var(--muted-foreground))]">
            <span className="inline-flex items-center gap-1.5">
              <Notebook size={14} /> {recap.note_count}{' '}
              {recap.note_count === 1 ? 'note' : 'notes'}
            </span>
            {recap.updated_at && (
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays size={14} /> Updated {formatDate(recap.updated_at)}
              </span>
            )}
          </div>
          {recap.intro ? (
            <p
              data-testid={RECAPS.detailIntro}
              className="mt-6 max-w-2xl text-[hsl(var(--muted-foreground))] whitespace-pre-line"
            >
              {recap.intro}
            </p>
          ) : null}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 pb-20">
        {notes.length === 0 ? (
          <div
            data-testid={RECAPS.emptyState}
            className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-10 text-center"
          >
            <p className="font-serif text-2xl">Nothing to recap here yet.</p>
            <p className="mt-2 text-[hsl(var(--muted-foreground))]">
              Field Notes from this month haven&apos;t been published.
            </p>
          </div>
        ) : (
          <ol className="space-y-6">
            {notes.map((n) => (
              <li key={n.id}>
                <Link
                  to={`/field-notes/${n.slug}`}
                  data-testid={RECAPS.noteRow}
                  className="group block"
                >
                  <Card className="overflow-hidden border-[hsl(var(--border))] card-elev card-elev-hover">
                    <div className="grid gap-0 sm:grid-cols-[220px_1fr]">
                      <div className="sm:min-h-[160px]">
                        <AspectRatio ratio={4 / 3} className="sm:h-full">
                          <img
                            src={absoluteImageUrl(n.hero_image?.url)}
                            alt={n.hero_image?.alt || n.title}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                            loading="lazy"
                          />
                        </AspectRatio>
                      </div>
                      <CardContent className="p-5 sm:p-6">
                        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                          <Badge variant="secondary" className="font-normal">
                            {labelFor(FIELD_NOTE_CATEGORIES, n.category)}
                          </Badge>
                          <span>•</span>
                          <time>{formatDate(n.date)}</time>
                        </div>
                        <h2 className="font-serif text-2xl leading-snug mt-2 tracking-tight">
                          {n.title}
                        </h2>
                        <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] line-clamp-3">
                          {n.excerpt}
                        </p>
                        <span className="mt-4 inline-flex items-center gap-1 text-sm text-[hsl(var(--primary))]">
                          Read note <ArrowRight size={14} />
                        </span>
                      </CardContent>
                    </div>
                  </Card>
                </Link>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
